/**
 * GrowthService — composes a goal's full cross-domain signal.
 *
 * It walks the relationship graph backwards from a goal to find the milestones,
 * targets, habits, and blocking problems linked to it, reads each one's current
 * state through the normal services, and folds them into a single calm rollup
 * via the pure `computeGoalRollup`.
 */

import {
  type EntityId,
  type FeatureServices,
  type GoalRollup,
  type HabitStateLite,
  type MilestoneStatusLite,
  type TargetStateLite,
  computeGoalRollup,
  isProblemOpen,
} from '@life-os/app-core';
import type { GraphService } from './graph-service.js';

export class GrowthService {
  constructor(
    private readonly services: FeatureServices,
    private readonly graph: GraphService,
  ) {}

  async goalRollup(goalId: EntityId): Promise<GoalRollup> {
    const goalRef = { kind: 'goal' as const, id: goalId };
    const incoming = await this.graph.backlinks(goalRef);

    // Milestones: linked by explicit contribution or a graph part_of edge.
    const milestones = await this.services.direction.listMilestones();
    const partOfIds = new Set(
      incoming.filter((r) => r.kind === 'part_of' && r.from.kind === 'milestone').map((r) => String(r.from.id)),
    );
    const milestoneStatuses: MilestoneStatusLite[] = milestones
      .filter((m) => m.contributesToGoalIds.some((g) => String(g) === String(goalId)) || partOfIds.has(String(m.id)))
      .map((m) => m.status);

    // Targets that support the goal.
    const targetIds = incoming.filter((r) => r.kind === 'supports' && r.from.kind === 'target').map((r) => r.from.id);
    const targetStates: TargetStateLite[] = await Promise.all(
      targetIds.map(async (id) => (await this.services.targets.consistency(id)).state),
    );

    // Habits that support the goal.
    const habitIds = incoming.filter((r) => r.kind === 'supports' && r.from.kind === 'habit').map((r) => r.from.id);
    const habitStates: HabitStateLite[] = await Promise.all(
      habitIds.map(async (id) => (await this.services.routines.habitSummary(id)).state),
    );

    // Open problems that block the goal.
    const blockerIds = new Set(
      incoming.filter((r) => r.kind === 'blocks' && r.from.kind === 'problem').map((r) => String(r.from.id)),
    );
    let openBlockers = 0;
    if (blockerIds.size > 0) {
      const problems = await this.services.problems.list();
      openBlockers = problems.filter((p) => blockerIds.has(String(p.id)) && isProblemOpen(p.status)).length;
    }

    return computeGoalRollup({ milestoneStatuses, targetStates, habitStates, openBlockers });
  }
}
